import { Box, Tooltip } from '@mui/material'
import React from 'react'
import Txt from './Txt'
import Card from './Card'

export type InfoEntry = { label: string; value: unknown; hint: string[] }

const sxRow = {
	display: 'grid',
	gridTemplateColumns: 'minmax(10rem, 20rem) 1fr',
	gap: '1rem',
	p: '0.6rem 0',
	borderBottom: '1px solid',
	borderColor: 'divider',
} as const

const sxHeadRow = { ...sxRow, borderColor: 'divider' } as const

const sxLabel = { m: 0, cursor: 'help', textDecoration: 'underline dotted' } as const

const sxValue = { m: 0 } as const

const formatValue = (value: unknown): React.ReactNode => {
	if (React.isValidElement(value)) return value
	if (value === null || value === undefined || value === '') return '-'
	if (typeof value === 'boolean') return value ? 'Ja' : 'Nein'
	if (Array.isArray(value)) return value.length ? value.join(', ') : '-'
	return String(value)
}

const sxBullets = { m: 0, pl: '1.1rem' } as const

const InfoRow = React.memo(({ label, value, hint }: InfoEntry): JSX.Element => {
	const title = React.useMemo(
		() => (
			<Box component="ul" sx={sxBullets}>
				{hint.map((point) => (
					<li key={point}>{point}</li>
				))}
			</Box>
		),
		[hint]
	)
	return (
		<Box sx={sxRow}>
			<Tooltip title={title} arrow placement="top-start">
				<Box component="span">
					<Txt variant="body2" color="text.secondary" sx={sxLabel}>
						{label}
					</Txt>
				</Box>
			</Tooltip>
			<Txt variant="body2" sx={sxValue}>
				{formatValue(value)}
			</Txt>
		</Box>
	)
})
InfoRow.displayName = 'InfoRow'

const InfoTable = ({
	title,
	entries,
	emptyHint = 'Keine Daten vorhanden.',
}: {
	title: string
	entries: InfoEntry[]
	emptyHint?: string
}): JSX.Element => (
	<Card>
		<Txt variant="h4" fontWeight="bold" m="0 0 1rem 0" color="primary.main">
			{title}
		</Txt>
		{entries.length ? (
			<>
				<Box sx={sxHeadRow}>
					<Txt variant="body2" color="grey.500" sx={sxValue}>
						Titel
					</Txt>
					<Txt variant="body2" color="grey.500" sx={sxValue}>
						Wert
					</Txt>
				</Box>
				{entries.map((entry) => (
					<InfoRow key={entry.label} label={entry.label} value={entry.value} hint={entry.hint} />
				))}
			</>
		) : (
			<Txt variant="body2" color="text.secondary">
				{emptyHint}
			</Txt>
		)}
	</Card>
)

export default React.memo(InfoTable)
